// CRM prospection (platform admin) — master-DB tables crm_contacts /
// crm_activities, locked to platform admins by RLS (is_admin()). Pure data
// access: status bookkeeping is computed in @erp/domain/crm by the caller.

import type { TypedSupabaseClient } from '../client';
import { orContainsAnyColumn } from './postgrest-search';
import type {
  ICrmRepository,
  CrmContact,
  CrmActivity,
  CreateCrmContactInput,
  UpdateCrmContactInput,
  CreateCrmActivityInput,
  CrmContactFilters,
  CrmSettings,
  UpdateCrmSettingsInput,
} from '../../../interfaces/src';
import type {
  CrmActivityType,
  CrmCallOutcome,
  CrmContactSource,
  CrmContactStatus,
  CrmGoalMetric,
  CrmPriority,
} from '@erp/domain/crm';

// crm_contacts / crm_activities ne sont pas encore dans les Database types —
// on casse la contrainte le temps que `supabase gen types` soit relancé.
const anyFrom = (client: TypedSupabaseClient) =>
  (table: string) => (client as unknown as { from: (t: string) => any }).from(table);

const ACTIVITY_SELECT = '*, crm_contacts(company_name)';
// Bulk insert par paquets — les listes de prospection peuvent faire
// plusieurs centaines de lignes.
const BULK_CHUNK = 500;

export class SupabaseCrmRepository implements ICrmRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  // ---------- Contacts ----------

  async listContacts(filters?: CrmContactFilters): Promise<CrmContact[]> {
    let q = anyFrom(this.supabase)('crm_contacts').select('*');
    if (filters?.status) q = q.eq('status', filters.status);
    if (filters?.source) q = q.eq('source', filters.source);
    if (filters?.priority) q = q.eq('priority', filters.priority);
    const search = filters?.search?.trim();
    if (search) {
      q = q.or(orContainsAnyColumn(['company_name', 'contact_name', 'phone', 'email', 'city'], search));
    }
    q = q.order('created_at', { ascending: false });
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []).map(mapContact);
  }

  async getContact(id: string): Promise<CrmContact | null> {
    const { data, error } = await anyFrom(this.supabase)('crm_contacts')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data ? mapContact(data) : null;
  }

  async createContact(input: CreateCrmContactInput): Promise<CrmContact> {
    const createdAt = new Date().toISOString();
    const initialStatus = input.status ?? 'new';
    const { data, error } = await anyFrom(this.supabase)('crm_contacts')
      .insert({
        company_name: input.companyName,
        contact_name: input.contactName ?? null,
        phone: input.phone ?? null,
        phone2: input.phone2 ?? null,
        email: input.email ?? null,
        website: input.website ?? null,
        city: input.city ?? null,
        address: input.address ?? null,
        sector: input.sector ?? null,
        source: input.source ?? 'cold_call',
        status: initialStatus,
        status_changed_at: createdAt,
        won_at: initialStatus === 'won' ? createdAt : null,
        priority: input.priority ?? 'medium',
        notes: input.notes ?? null,
        next_action_at: input.nextActionAt ?? null,
        next_action_note: input.nextActionNote ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return mapContact(data);
  }

  async createContacts(inputs: CreateCrmContactInput[]): Promise<CrmContact[]> {
    const created: CrmContact[] = [];
    for (let i = 0; i < inputs.length; i += BULK_CHUNK) {
      const chunk = inputs.slice(i, i + BULK_CHUNK).map((input) => ({
        company_name: input.companyName,
        contact_name: input.contactName ?? null,
        phone: input.phone ?? null,
        phone2: input.phone2 ?? null,
        email: input.email ?? null,
        website: input.website ?? null,
        city: input.city ?? null,
        address: input.address ?? null,
        sector: input.sector ?? null,
        source: input.source ?? 'cold_call',
        status: input.status ?? 'new',
        priority: input.priority ?? 'medium',
        notes: input.notes ?? null,
        next_action_at: input.nextActionAt ?? null,
        next_action_note: input.nextActionNote ?? null,
      }));
      const { data, error } = await anyFrom(this.supabase)('crm_contacts')
        .insert(chunk)
        .select();
      if (error) throw error;
      created.push(...(data ?? []).map(mapContact));
    }
    return created;
  }

  async updateContact(id: string, input: UpdateCrmContactInput): Promise<CrmContact> {
    const patch: Record<string, unknown> = {};
    if (input.companyName !== undefined) patch['company_name'] = input.companyName;
    if (input.contactName !== undefined) patch['contact_name'] = input.contactName;
    if (input.phone !== undefined) patch['phone'] = input.phone;
    if (input.phone2 !== undefined) patch['phone2'] = input.phone2;
    if (input.email !== undefined) patch['email'] = input.email;
    if (input.website !== undefined) patch['website'] = input.website;
    if (input.city !== undefined) patch['city'] = input.city;
    if (input.address !== undefined) patch['address'] = input.address;
    if (input.sector !== undefined) patch['sector'] = input.sector;
    if (input.source !== undefined) patch['source'] = input.source;
    if (input.priority !== undefined) patch['priority'] = input.priority;
    if (input.notes !== undefined) patch['notes'] = input.notes;
    if (input.nextActionAt !== undefined) patch['next_action_at'] = input.nextActionAt;
    if (input.nextActionNote !== undefined) patch['next_action_note'] = input.nextActionNote;
    if (input.lostReason !== undefined) patch['lost_reason'] = input.lostReason;
    if (input.convertedTenantId !== undefined) patch['converted_tenant_id'] = input.convertedTenantId;
    if (input.status !== undefined) patch['status'] = input.status;
    if (input.statusChangedAt !== undefined) patch['status_changed_at'] = input.statusChangedAt;
    if (input.wonAt !== undefined) patch['won_at'] = input.wonAt;

    const { data, error } = await anyFrom(this.supabase)('crm_contacts')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapContact(data);
  }

  async deleteContact(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('crm_contacts').delete().eq('id', id);
    if (error) throw error;
  }

  // ---------- Activities ----------

  async listContactActivities(contactId: string): Promise<CrmActivity[]> {
    const { data, error } = await anyFrom(this.supabase)('crm_activities')
      .select(ACTIVITY_SELECT)
      .eq('contact_id', contactId)
      .order('happened_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapActivity);
  }

  async listRecentActivities(limit = 100): Promise<CrmActivity[]> {
    const { data, error } = await anyFrom(this.supabase)('crm_activities')
      .select(ACTIVITY_SELECT)
      .order('happened_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data ?? []).map(mapActivity);
  }

  async addActivity(input: CreateCrmActivityInput): Promise<CrmActivity> {
    const row: Record<string, unknown> = {
      contact_id: input.contactId,
      type: input.type,
      outcome: input.outcome ?? null,
      notes: input.notes ?? null,
    };
    if (input.happenedAt !== undefined) row['happened_at'] = input.happenedAt;
    const { data, error } = await anyFrom(this.supabase)('crm_activities')
      .insert(row)
      .select(ACTIVITY_SELECT)
      .single();
    if (error) throw error;
    return mapActivity(data);
  }

  async deleteActivity(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('crm_activities').delete().eq('id', id);
    if (error) throw error;
  }

  // ---------- Settings (objectifs hebdo, singleton) ----------

  async getSettings(): Promise<CrmSettings> {
    const { data, error } = await anyFrom(this.supabase)('crm_settings')
      .select('*')
      .maybeSingle();
    if (error) throw error;
    return data ? mapSettings(data) : { goals: [] };
  }

  async updateSettings(input: UpdateCrmSettingsInput): Promise<CrmSettings> {
    const patch: Record<string, unknown> = {};
    if (input.goals !== undefined) {
      patch['goals'] = input.goals.map((g) => ({ metric: g.metric, target: g.target }));
    }

    const { data: existing, error: readError } = await anyFrom(this.supabase)('crm_settings')
      .select('id')
      .maybeSingle();
    if (readError) throw readError;

    const query = existing
      ? anyFrom(this.supabase)('crm_settings').update(patch).eq('id', existing.id)
      : anyFrom(this.supabase)('crm_settings').insert(patch);
    const { data, error } = await query.select().single();
    if (error) throw error;
    return mapSettings(data);
  }
}

// Tolérant à la migration : si la colonne `goals` n'existe pas encore (déploiement
// avant migration), on dérive la liste des deux colonnes scalaires historiques.
function mapSettings(row: Record<string, unknown>): CrmSettings {
  const raw = row['goals'];
  if (Array.isArray(raw)) {
    const goals = raw
      .filter((g): g is { metric: CrmGoalMetric; target: number } =>
        !!g && typeof (g as { metric?: unknown }).metric === 'string' &&
        Number((g as { target?: unknown }).target) > 0,
      )
      .map((g) => ({ metric: g.metric, target: Number(g.target) }));
    return { goals };
  }
  const legacy: CrmSettings['goals'] = [];
  const calls = Number(row['weekly_call_goal'] ?? 0);
  const newContacts = Number(row['weekly_new_contacts_goal'] ?? 0);
  if (calls > 0) legacy.push({ metric: 'calls', target: calls });
  if (newContacts > 0) legacy.push({ metric: 'new_contacts', target: newContacts });
  return { goals: legacy };
}

function mapContact(row: Record<string, unknown>): CrmContact {
  return {
    id: row['id'] as string,
    companyName: row['company_name'] as string,
    contactName: (row['contact_name'] as string | null) ?? null,
    phone: (row['phone'] as string | null) ?? null,
    phone2: (row['phone2'] as string | null) ?? null,
    email: (row['email'] as string | null) ?? null,
    website: (row['website'] as string | null) ?? null,
    city: (row['city'] as string | null) ?? null,
    address: (row['address'] as string | null) ?? null,
    sector: (row['sector'] as string | null) ?? null,
    source: row['source'] as CrmContactSource,
    status: row['status'] as CrmContactStatus,
    priority: row['priority'] as CrmPriority,
    notes: (row['notes'] as string | null) ?? null,
    nextActionAt: (row['next_action_at'] as string | null) ?? null,
    nextActionNote: (row['next_action_note'] as string | null) ?? null,
    statusChangedAt: row['status_changed_at'] as string,
    wonAt: (row['won_at'] as string | null) ?? null,
    lostReason: (row['lost_reason'] as string | null) ?? null,
    convertedTenantId: (row['converted_tenant_id'] as string | null) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapActivity(row: Record<string, unknown>): CrmActivity {
  const joined = row['crm_contacts'] as { company_name?: string } | null;
  return {
    id: row['id'] as string,
    contactId: row['contact_id'] as string,
    contactCompanyName: joined?.company_name ?? null,
    type: row['type'] as CrmActivityType,
    outcome: (row['outcome'] as CrmCallOutcome | null) ?? null,
    notes: (row['notes'] as string | null) ?? null,
    happenedAt: row['happened_at'] as string,
    createdAt: row['created_at'] as string,
  };
}
